import * as React from "react";
import { useVirtualizer } from "@tanstack/react-virtual";
import { ProgressCircle } from "@adobe/react-spectrum";
import { Input, TextField } from "react-aria-components";
import { useDependency } from "../../../../contexts/DependencyProvider";
import { usePreloadAssets } from "../../../../hooks/UsePreloadAssets";
import type {
  PathSegmentContextRef,
  SearchResultItemData,
} from "../../../../infra/protected/Search/SearchResultData";
import type { GroupedSearchResultItemData } from "../../../../utils/SearchUtils";
import { GetContextPathRequest } from "../../../../services/soap/project/requests/GetContextPathRequest";
import type { Type as FolderType } from "../../../../services/soap/project/responses/ListFolderTypeResponse";
import { contextObjectFromGetContextPathList } from "./contextPathToContextObject";
import {
  contextObjectFromSearchItem,
  parentForLeafChildren,
  parentForSiblingBrowse,
  pathSegmentsFromSearchItem,
  type ContextSearchScope,
  type ContextObject,
  type ContextSelectionProps,
} from "./types";
import { useContextSearchState } from "./useContextSearchState";
import {
  ADD_FOOTER_HEIGHT,
  HEADER_HEIGHT,
  ROW_HEIGHT,
  styles,
} from "./ContextSelection.styles";
import { highlightMatch } from "./highlightMatch";
import {
  ActivityIcon,
  ChevronRightIcon,
  ClientIcon,
  ProjectIcon,
} from "../../Icons/IconsLib";

const PATH_SEGMENT_ICON_SIZE = 14;

type FolderTypeLookup = Record<string, Partial<FolderType> & { id: number; name: string }>;

type BreadcrumbDisplayData = {
  prefixRef: string | null;
  folderTypeLabel: string | null;
  segments: string[];
  segmentContexts?: PathSegmentContextRef[];
  pathText: string | null;
};

function refIdPrefixForDisplay(
  refId: string | number | undefined
): string {
  if (refId == null) {
    return "";
  }
  if (typeof refId === "number") {
    if (!Number.isFinite(refId) || refId <= 0) {
      return "";
    }
    return `(${refId})`;
  }
  const s = refId.trim();
  return s ? `(${s})` : "";
}

function normalizeFolderTypesLookup(folderTypes: unknown): FolderTypeLookup {
  if (!folderTypes || typeof folderTypes !== "object" || Array.isArray(folderTypes)) {
    return {};
  }
  return folderTypes as FolderTypeLookup;
}

/**
 * For ListFPA folder rows, `Folder.folderType` (folder type id, string in SOAP) is stored on
 * `SearchResultItemData.contextTypeId` by SearchUtils `convertToSearchResultItemData`.
 */
function folderTypeIdFromListFpaFolderItem(
  item: SearchResultItemData
): number | undefined {
  if ((item.itemType ?? "").toUpperCase() !== "FOLDER") {
    return undefined;
  }
  const id = Number(item.contextTypeId);
  if (!Number.isFinite(id) || id <= 0) {
    return undefined;
  }
  return id;
}

function folderTypeDisplayName(
  folderTypeId: number | undefined,
  folderTypes: FolderTypeLookup
): string | null {
  if (folderTypeId == null || folderTypeId <= 0) {
    return null;
  }
  const t = folderTypes[String(folderTypeId)];
  const name = t?.name?.trim();
  return name || null;
}

function currentRefId(
  segmentContexts: PathSegmentContextRef[] | undefined
): string | number | undefined {
  if (!segmentContexts?.length) {
    return undefined;
  }
  return segmentContexts[segmentContexts.length - 1]?.refId;
}

function buildBreadcrumbDisplayData({
  refId,
  folderTypeLabel,
  segments,
  fallback,
  segmentContexts,
}: {
  refId: string | number | undefined;
  folderTypeLabel: string | null;
  segments: string[];
  fallback: string | null;
  segmentContexts?: PathSegmentContextRef[];
}): BreadcrumbDisplayData {
  const cleanedSegments = segments.filter(Boolean);
  const parts: string[] = [];
  if (folderTypeLabel) {
    parts.push(folderTypeLabel);
  }
  parts.push(...cleanedSegments);
  const refPrefix = refIdPrefixForDisplay(refId) || null;
  if (!refPrefix && parts.length === 0) {
    return {
      prefixRef: null,
      folderTypeLabel: folderTypeLabel ?? null,
      segments: cleanedSegments,
      segmentContexts,
      pathText: fallback,
    };
  }
  const breadcrumbCore = parts.join(" > ");
  const pathText = refPrefix
    ? `${refPrefix}${breadcrumbCore ? ` ${breadcrumbCore}` : ""}`
    : breadcrumbCore;
  return {
    prefixRef: refPrefix,
    folderTypeLabel: folderTypeLabel ?? null,
    segments: cleanedSegments,
    segmentContexts,
    pathText: pathText || fallback,
  };
}

function leafTypeFromSearchItem(
  item: SearchResultItemData
): PathSegmentContextRef["type"] | undefined {
  const pcs = item.pathSegmentContexts;
  if (pcs?.length) {
    return pcs[pcs.length - 1]?.type;
  }
  const t = (item.itemType ?? "").toUpperCase();
  if (t === "FOLDER" || t === "PROJECT" || t === "ACTIVITY") {
    return t;
  }
  return undefined;
}

function leafTypeFromContextObject(
  context: ContextObject
): PathSegmentContextRef["type"] | undefined {
  const pcs = context.pathSegmentContexts;
  if (pcs?.length) {
    return pcs[pcs.length - 1]?.type;
  }
  const t = context.contextType;
  if (t === "FOLDER" || t === "PROJECT" || t === "ACTIVITY") {
    return t;
  }
  return undefined;
}

function PathSegmentTypeIcon({
  contextType,
  iconSize = PATH_SEGMENT_ICON_SIZE,
  isListItem = false,
}: {
  contextType: PathSegmentContextRef["type"] | undefined;
  iconSize?: number;
  isListItem?: boolean;
}) {
  if (contextType === "FOLDER") {
    return (
      <span className={styles.viewSegmentIcon + " " + (isListItem ? styles.viewSegmentIconListItem : "")} aria-hidden>
        <ClientIcon size={iconSize} />
      </span>
    );
  }
  if (contextType === "PROJECT") {
    return (
      <span className={styles.viewSegmentIcon + " " + (isListItem ? styles.viewSegmentIconListItem : "")} aria-hidden>
        <ProjectIcon size={iconSize} />
      </span>
    );
  }
  if (contextType === "ACTIVITY") {
    return (
      <span className={styles.viewSegmentIcon + " " + (isListItem ? styles.viewSegmentIconListItem : "")} aria-hidden>
        <ActivityIcon size={iconSize} />
      </span>
    );
  }
  return null;
}

type VirtualRow =
  | { kind: "header"; title: string; key: string }
  | {
      kind: "item";
      item: SearchResultItemData;
      display: BreadcrumbDisplayData;
      leafType: PathSegmentContextRef["type"] | undefined;
      flatIndex: number;
      key: string;
    };

/** Match query only against hierarchy names (breadcrumbs + title), not ref ids (subtitle). */
function itemMatchesNameQuery(
  item: SearchResultItemData,
  queryTrimmed: string
): boolean {
  if (!queryTrimmed) {
    return true;
  }
  const q = queryTrimmed.toLowerCase();
  const segments = normalizeFolderSegments(item, pathSegmentsFromSearchItem(item));
  return segments.some((s) => (s ?? "").toLowerCase().includes(q));
}

function filterSearchResultsByNameOnly(
  groups: GroupedSearchResultItemData[],
  queryTrimmed: string
): GroupedSearchResultItemData[] {
  if (!queryTrimmed) {
    return groups;
  }
  return groups
    .map((g) => ({
      ...g,
      items: g.items.filter((it) => itemMatchesNameQuery(it, queryTrimmed)),
    }))
    .filter((g) => g.items.length > 0);
}

function normalizeFolderSegments(
  item: SearchResultItemData,
  segments: string[]
): string[] {
  if ((item.itemType ?? "").toUpperCase() !== "FOLDER") {
    return segments;
  }
  return segments.length > 1 ? segments.slice(1) : segments;
}

function normalizeFolderPathSegmentContexts(
  item: SearchResultItemData,
  pathSegmentContexts: PathSegmentContextRef[] | undefined
): PathSegmentContextRef[] | undefined {
  if ((item.itemType ?? "").toUpperCase() !== "FOLDER" || !pathSegmentContexts?.length) {
    return pathSegmentContexts;
  }
  return pathSegmentContexts.length > 1
    ? pathSegmentContexts.slice(1)
    : pathSegmentContexts;
}

function contextObjectFromNormalizedSearchItem(item: SearchResultItemData): ContextObject {
  const base = contextObjectFromSearchItem(item);
  return {
    ...base,
    pathSegments: normalizeFolderSegments(item, base.pathSegments),
    ...(base.pathSegmentContexts?.length
      ? {
          pathSegmentContexts: normalizeFolderPathSegmentContexts(
            item,
            base.pathSegmentContexts
          ),
        }
      : {}),
  };
}

function buildVirtualRows(
  searchResults: { title: string; items: SearchResultItemData[] }[],
  searchTitle: string,
  folderTypes: FolderTypeLookup
): { rows: VirtualRow[]; flatItems: SearchResultItemData[] } {
  const flatItems: SearchResultItemData[] = [];
  const rows: VirtualRow[] = [];
  let fi = 0;

  for (let gi = 0; gi < searchResults.length; gi++) {
    const g = searchResults[gi];
    const headerTitle =
      g.title ||
      (searchTitle.startsWith("History") ? "Recent" : "Results");
    rows.push({
      kind: "header",
      title: headerTitle,
      key: `hdr-${gi}-${headerTitle}`,
    });
    for (const it of g.items) {
      const segments = normalizeFolderSegments(it, pathSegmentsFromSearchItem(it));
      const segmentContexts = normalizeFolderPathSegmentContexts(
        it,
        it.pathSegmentContexts
      );
      const listFolderTypeLabel = folderTypeDisplayName(
        folderTypeIdFromListFpaFolderItem(it),
        folderTypes
      );
      rows.push({
        kind: "item",
        item: it,
        leafType: leafTypeFromSearchItem(it),
        display: buildBreadcrumbDisplayData({
          refId: currentRefId(segmentContexts),
          folderTypeLabel: listFolderTypeLabel,
          segments,
          fallback: it.itemId ? String(it.itemId) : "—",
          segmentContexts,
        }),
        flatIndex: fi,
        key: `${it.html_id}_${fi}`,
      });
      flatItems.push(it);
      fi++;
    }
  }

  return { rows, flatItems };
}

export function ContextSelection({
  value,
  onSelectionChange,
  folderStatusMap,
  projectStatusMap,
  activityStatusMap,
  isReadOnly = false,
  maxVisibleItems = 5,
  showBorders = true,
  showBorder,
  allowTransparentBG = false,
  filledBackground,
  allowClearSelection = false,
  allowAdd = false,
  onAdd,
  defaultValue,
  explicitContextId,
  onExplicitContextResolveError,
  startInEditMode = false,
  className,
  "aria-label": ariaLabel = "Select context",
}: ContextSelectionProps) {
  const effectiveShowBorders = showBorder ?? showBorders;
  const effectiveAllowTransparentBG =
    allowTransparentBG ||
    (filledBackground === undefined ? false : !filledBackground);

  const { projectService, store } = useDependency();
  const { folderTypes } = usePreloadAssets();
  const folderTypesLookup = React.useMemo(
    () => normalizeFolderTypesLookup(folderTypes),
    [folderTypes]
  );
  const {
    searchTitle,
    searchResults,
    handleSearchChangeDebounced,
    resetAndLoadInitial,
    loadMore,
  } = useContextSearchState({
    folderStatusMap,
    projectStatusMap,
    activityStatusMap,
  });

  const [isEditing, setIsEditing] = React.useState(startInEditMode);
  const [isResolvingExplicitContext, setIsResolvingExplicitContext] =
    React.useState(false);
  const [inputValue, setInputValue] = React.useState("");
  const [activeFlatIndex, setActiveFlatIndex] = React.useState(0);

  const rootRef = React.useRef<HTMLDivElement>(null);
  const scrollRef = React.useRef<HTMLDivElement>(null);
  const editSessionStartedRef = React.useRef(false);
  const pendingInitialScopeRef = React.useRef<ContextSearchScope>({
    parent: null,
  });
  const explicitResolveRunIdRef = React.useRef(0);
  const defaultResolveRunIdRef = React.useRef(0);
  /** Snapshot of props from first render — defaultValue / explicitContextId apply only here. */
  const defaultContextIdOnMountRef = React.useRef(defaultValue);
  const explicitContextIdOnMountRef = React.useRef(explicitContextId);
  const defaultValueResolveDoneRef = React.useRef(false);
  const valueRef = React.useRef(value);
  valueRef.current = value;
  const onSelectionChangeRef = React.useRef(onSelectionChange);
  onSelectionChangeRef.current = onSelectionChange;
  const onExplicitContextResolveErrorRef = React.useRef(
    onExplicitContextResolveError
  );
  onExplicitContextResolveErrorRef.current = onExplicitContextResolveError;
  const listboxId = React.useId();

  const queryTrimmed = inputValue.trim();

  const filteredSearchResults = React.useMemo(
    () => filterSearchResultsByNameOnly(searchResults, queryTrimmed),
    [searchResults, queryTrimmed]
  );

  const { rows: virtualRows, flatItems } = React.useMemo(
    () => buildVirtualRows(filteredSearchResults, searchTitle, folderTypesLookup),
    [filteredSearchResults, searchTitle, folderTypesLookup]
  );

  React.useEffect(() => {
    if (explicitContextId == null || explicitContextId <= 0) {
      setIsResolvingExplicitContext(false);
      return;
    }

    const v = valueRef.current;
    if (
      v &&
      v.contextId !== explicitContextId &&
      v.contextId > 0
    ) {
      // Parent still passes an explicit id, but the user chose a different context — do not overwrite.
      setIsResolvingExplicitContext(false);
      return;
    }

    if (
      v &&
      v.contextId === explicitContextId &&
      v.pathSegments.length > 0 &&
      v.pathSegmentContexts?.length === v.pathSegments.length
    ) {
      setIsResolvingExplicitContext(false);
      return;
    }

    let cancelled = false;
    const runId = ++explicitResolveRunIdRef.current;
    setIsResolvingExplicitContext(true);

    void (async () => {
      try {
        const res = await projectService.getContextPath(
          new GetContextPathRequest(
            store.Server,
            store.SessionId,
            explicitContextId
          )
        );
        if (cancelled || runId !== explicitResolveRunIdRef.current) {
          return;
        }
        if (!res.LIST?.length || res.count === 0) {
          onExplicitContextResolveErrorRef.current?.(
            new Error("GetContextPath returned no context path")
          );
          return;
        }
        const ctx = contextObjectFromGetContextPathList(res.LIST);
        if (!ctx) {
          onExplicitContextResolveErrorRef.current?.(
            new Error("Could not map GetContextPath LIST to context object")
          );
          return;
        }
        onSelectionChangeRef.current(ctx);
      } catch (e) {
        if (!cancelled && runId === explicitResolveRunIdRef.current) {
          onExplicitContextResolveErrorRef.current?.(e);
        }
      } finally {
        if (runId === explicitResolveRunIdRef.current) {
          setIsResolvingExplicitContext(false);
        }
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [
    explicitContextId,
    store.Server,
    store.SessionId,
    projectService,
    value?.contextId,
  ]);

  /** One-shot: resolve `defaultValue` (first render only) when no explicit id on mount and no value yet. */
  React.useEffect(() => {
    if (defaultValueResolveDoneRef.current) {
      return;
    }

    const explicitAtMount = explicitContextIdOnMountRef.current;
    if (explicitAtMount != null && explicitAtMount > 0) {
      defaultValueResolveDoneRef.current = true;
      return;
    }

    const defId = defaultContextIdOnMountRef.current;
    if (defId == null || defId <= 0) {
      defaultValueResolveDoneRef.current = true;
      return;
    }

    if (valueRef.current?.contextId) {
      defaultValueResolveDoneRef.current = true;
      return;
    }

    defaultValueResolveDoneRef.current = true;

    const runId = ++defaultResolveRunIdRef.current;
    setIsResolvingExplicitContext(true);

    void (async () => {
      try {
        const res = await projectService.getContextPath(
          new GetContextPathRequest(store.Server, store.SessionId, defId)
        );
        if (runId !== defaultResolveRunIdRef.current) {
          return;
        }
        if (!res.LIST?.length || res.count === 0) {
          onExplicitContextResolveErrorRef.current?.(
            new Error("GetContextPath returned no context path")
          );
          return;
        }
        const ctx = contextObjectFromGetContextPathList(res.LIST);
        if (!ctx) {
          onExplicitContextResolveErrorRef.current?.(
            new Error("Could not map GetContextPath LIST to context object")
          );
          return;
        }
        onSelectionChangeRef.current(ctx);
      } catch (e) {
        if (runId === defaultResolveRunIdRef.current) {
          onExplicitContextResolveErrorRef.current?.(e);
        }
      } finally {
        if (runId === defaultResolveRunIdRef.current) {
          setIsResolvingExplicitContext(false);
        }
      }
    })();

    return () => {
      setIsResolvingExplicitContext(false);
    };
  }, [projectService, store.Server, store.SessionId]);

  React.useEffect(() => {
    if (!isEditing) {
      editSessionStartedRef.current = false;
      return;
    }
    if (editSessionStartedRef.current) {
      return;
    }
    editSessionStartedRef.current = true;
    resetAndLoadInitial(pendingInitialScopeRef.current);
    setInputValue("");
    setActiveFlatIndex(0);
  }, [isEditing, resetAndLoadInitial]);

  React.useEffect(() => {
    setActiveFlatIndex((i) => {
      if (flatItems.length === 0) {
        return 0;
      }
      return Math.min(i, flatItems.length - 1);
    });
  }, [flatItems.length, searchResults]);

  const maxListHeight = maxVisibleItems * ROW_HEIGHT;

  const showAddFooter =
    allowAdd &&
    !isReadOnly &&
    isEditing &&
    (searchTitle.startsWith("Results") || searchTitle.startsWith("Browse"));

  const listScrollMaxHeight = showAddFooter
    ? Math.max(0, maxListHeight - ADD_FOOTER_HEIGHT)
    : maxListHeight;

  const rowVirtualizer = useVirtualizer({
    count: virtualRows.length,
    getScrollElement: () => scrollRef.current,
    estimateSize: React.useCallback(
      (index: number) =>
        virtualRows[index]?.kind === "header" ? HEADER_HEIGHT : ROW_HEIGHT,
      [virtualRows]
    ),
    overscan: 10,
  });

  React.useEffect(() => {
    if (!isEditing || flatItems.length === 0) {
      return;
    }
    const rowIndex = virtualRows.findIndex(
      (r) => r.kind === "item" && r.flatIndex === activeFlatIndex
    );
    if (rowIndex >= 0) {
      rowVirtualizer.scrollToIndex(rowIndex, { align: "auto" });
    }
  }, [activeFlatIndex, flatItems.length, isEditing, virtualRows, rowVirtualizer]);

  const selectItem = React.useCallback(
    (item: SearchResultItemData) => {
      onSelectionChange(contextObjectFromNormalizedSearchItem(item));
      setIsEditing(false);
      setInputValue("");
    },
    [onSelectionChange]
  );

  const exitEditMode = React.useCallback(() => {
    setIsEditing(false);
    setInputValue("");
  }, []);

  const prevExternalContextIdRef = React.useRef<number | null | undefined>(undefined);
  React.useEffect(() => {
    const cur = value?.contextId ?? null;
    if (prevExternalContextIdRef.current === undefined) {
      prevExternalContextIdRef.current = cur;
      return;
    }
    if (prevExternalContextIdRef.current !== cur && isEditing) {
      exitEditMode();
    }
    prevExternalContextIdRef.current = cur;
  }, [value?.contextId, isEditing, exitEditMode]);

  React.useEffect(() => {
    if (!isEditing) {
      return;
    }
    const onDocMouseDown = (e: MouseEvent) => {
      if (rootRef.current && !rootRef.current.contains(e.target as Node)) {
        exitEditMode();
      }
    };
    document.addEventListener("mousedown", onDocMouseDown);
    return () => document.removeEventListener("mousedown", onDocMouseDown);
  }, [isEditing, exitEditMode]);

  const onScrollLoadMore = React.useCallback(
    (e: React.UIEvent<HTMLDivElement>) => {
      const el = e.currentTarget;
      if (el.scrollTop + el.clientHeight >= el.scrollHeight - 24) {
        loadMore();
      }
    },
    [loadMore]
  );

  const handleInputChange = (v: string) => {
    setInputValue(v);
    handleSearchChangeDebounced(v);
  };

  const handleInputKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === "Escape") {
      e.preventDefault();
      exitEditMode();
      return;
    }
    if (e.key === "ArrowDown") {
      e.preventDefault();
      if (flatItems.length === 0) {
        return;
      }
      setActiveFlatIndex((i) => Math.min(flatItems.length - 1, i + 1));
      return;
    }
    if (e.key === "ArrowUp") {
      e.preventDefault();
      if (flatItems.length === 0) {
        return;
      }
      setActiveFlatIndex((i) => Math.max(0, i - 1));
      return;
    }
    if (e.key === "Enter") {
      e.preventDefault();
      const item = flatItems[activeFlatIndex];
      if (item) {
        selectItem(item);
      }
    }
  };

  const selectedDisplay = React.useMemo(() => {
    if (!value) {
      return null;
    }
    return buildBreadcrumbDisplayData({
      refId: currentRefId(value.pathSegmentContexts),
      folderTypeLabel: folderTypeDisplayName(value.folderTypeId, folderTypesLookup),
      segments: value.pathSegments,
      fallback: value.contextId ? String(value.contextId) : null,
      segmentContexts: value.pathSegmentContexts,
    });
  }, [value, folderTypesLookup]);

  const leafChildrenParent = React.useMemo(
    () => (value ? parentForLeafChildren(value) : null),
    [value]
  );

  const openEditMode = React.useCallback(
    (scope: ContextSearchScope) => {
      if (isReadOnly) {
        return;
      }
      pendingInitialScopeRef.current = scope;
      setIsEditing(true);
    },
    [isReadOnly]
  );

  const clearSelection = React.useCallback(
    (e: React.MouseEvent) => {
      e.preventDefault();
      e.stopPropagation();
      onSelectionChange(null);
    },
    [onSelectionChange]
  );

  return (
    <div
      ref={rootRef}
      className={[styles.root, isEditing && styles.rootIsEditing, className]
        .filter(Boolean)
        .join(" ")}
    >
      {!isEditing ? (
        <div
          role="group"
          aria-label={ariaLabel}
          className={[
            styles.viewRow,
            !effectiveShowBorders && styles.viewRowBorderless,
            effectiveAllowTransparentBG && styles.viewRowTransparentBg,
          ]
            .filter(Boolean)
            .join(" ")}
        >
          <div className={styles.viewRowMain}>
            {isResolvingExplicitContext ? (
              <div
                className={styles.viewLoading}
                role="status"
                aria-live="polite"
              >
                <ProgressCircle
                  size="S"
                  aria-label="Loading context"
                  isIndeterminate
                />
                <span className={styles.viewLoadingText}>
                  Loading context…
                </span>
              </div>
            ) : selectedDisplay?.pathText && value ? (
              selectedDisplay.segments.length > 0 ? (
                <div className={styles.viewPath} title={selectedDisplay.pathText ?? undefined}>
                  <button
                    type="button"
                    className={styles.viewPrefix}
                    disabled={isReadOnly}
                    aria-label={`${ariaLabel}: search everywhere`}
                    onClick={() => openEditMode({ parent: null })}
                  >
                    <PathSegmentTypeIcon
                      contextType={leafTypeFromContextObject(value)}
                    />
                  </button>
                  {selectedDisplay.prefixRef ? (
                    <span className={styles.viewSegmentStatic}>
                      {selectedDisplay.prefixRef}
                    </span>
                  ) : null}
                  {selectedDisplay.folderTypeLabel ? (
                    <span className={styles.viewSegmentStatic}>
                      {selectedDisplay.folderTypeLabel}
                    </span>
                  ) : null}
                  {selectedDisplay.segments.map((segment, i) => {
                    const pc = selectedDisplay.segmentContexts?.[i];
                    const showChevron = !!selectedDisplay.folderTypeLabel || i > 0;
                    return (
                      <React.Fragment key={`seg-${i}-${segment}`}>
                        {showChevron ? (
                          <span className={styles.viewPathSep} aria-hidden>
                            <ChevronRightIcon
                              size="S"
                              UNSAFE_className={styles.viewPathChevron}
                            />
                          </span>
                        ) : null}
                        {pc ? (
                          <button
                            type="button"
                            className={styles.viewSegment}
                            disabled={isReadOnly}
                            aria-label={`Show items at the same level as ${segment}`}
                            onClick={() =>
                              openEditMode({
                                parent: parentForSiblingBrowse(
                                  i,
                                  selectedDisplay.segmentContexts
                                ),
                                targetItemType: pc.type,
                              })
                            }
                          >
                            <span className={styles.viewSegmentText}>
                              {segment}
                            </span>
                          </button>
                        ) : (
                          <span className={styles.viewSegmentStatic}>
                            {segment}
                          </span>
                        )}
                      </React.Fragment>
                    );
                  })}
                  {leafChildrenParent ? (
                    <>
                      <span className={styles.viewPathSep} aria-hidden>
                        <ChevronRightIcon
                          size="S"
                          UNSAFE_className={styles.viewPathChevron}
                        />
                      </span>
                      <button
                        type="button"
                        className={styles.viewSegmentQuickAdd}
                        disabled={isReadOnly}
                        aria-label={`${ariaLabel}: browse or select child context`}
                        onClick={() =>
                          openEditMode({
                            parent: leafChildrenParent,
                          })
                        }
                      >
                        …
                      </button>
                    </>
                  ) : null}
                </div>
              ) : (
                <div className={styles.viewPath} title={selectedDisplay.pathText ?? undefined}>
                  <button
                    type="button"
                    className={styles.viewPrefix}
                    disabled={isReadOnly}
                    aria-label={`${ariaLabel}: search everywhere`}
                    onClick={() => openEditMode({ parent: null })}
                  >
                    <PathSegmentTypeIcon
                      contextType={leafTypeFromContextObject(value)}
                    />
                  </button>
                  {selectedDisplay.prefixRef ? (
                    <span className={styles.viewSegmentStatic}>
                      {selectedDisplay.prefixRef}
                    </span>
                  ) : null}
                  {selectedDisplay.folderTypeLabel ? (
                    <span className={styles.viewSegmentStatic}>
                      {selectedDisplay.folderTypeLabel}
                    </span>
                  ) : null}
                </div>
              )
            ) : (
              <button
                type="button"
                className={styles.viewPlaceholderButton}
                aria-label={ariaLabel}
                aria-expanded={false}
                disabled={isReadOnly}
                onClick={() => openEditMode({ parent: null })}
              >
                <span className={styles.viewButtonPlaceholder}>
                  No context selected
                </span>
              </button>
            )}
          </div>
          {allowClearSelection && value ? (
            <button
              type="button"
              className={styles.viewClearButton}
              aria-label="Clear context selection"
              disabled={isReadOnly}
              onClick={clearSelection}
            >
              <svg
                className={styles.viewClearIcon}
                viewBox="0 0 24 24"
                aria-hidden
                focusable="false"
              >
                <path
                  fill="currentColor"
                  d="M19 6.41L17.59 5 12 10.59 6.41 5 5 6.41 10.59 12 5 17.59 6.41 19 12 13.41 17.59 19 19 17.59 13.41 12z"
                />
              </svg>
            </button>
          ) : null}
        </div>
      ) : (
        <div
          className={[
            styles.editRoot,
            !effectiveShowBorders && styles.editRootBorderless,
          ]
            .filter(Boolean)
            .join(" ")}
        >
          <div className={styles.editHeader}>
            <div
              className={[
                styles.searchField,
                !effectiveShowBorders && styles.searchFieldBorderless,
              ]
                .filter(Boolean)
                .join(" ")}
            >
              <TextField aria-label={ariaLabel}>
                <Input
                  className={[
                    styles.searchInput,
                    !effectiveShowBorders && styles.searchInputBorderless,
                  ]
                    .filter(Boolean)
                    .join(" ")}
                  value={inputValue}
                  autoFocus
                  aria-controls={listboxId}
                  aria-expanded
                  aria-autocomplete="list"
                  onChange={(e) => handleInputChange(e.target.value)}
                  onKeyDown={handleInputKeyDown}
                />
              </TextField>
            </div>
            <div className={styles.metaLine}>{searchTitle}</div>
          </div>
          <div className={styles.listColumnOverlay}>
            <div
              ref={scrollRef}
              className={styles.listScroll}
              style={{ maxHeight: listScrollMaxHeight }}
              onScroll={onScrollLoadMore}
            >
              {virtualRows.length === 0 ? (
                <div className={styles.emptyHint}>No results</div>
              ) : (
                <div
                  className={styles.listInner}
                  style={{ height: rowVirtualizer.getTotalSize() }}
                  id={listboxId}
                  role="listbox"
                  aria-label="Context search results"
                >
                  {rowVirtualizer.getVirtualItems().map((vi) => {
                    const row = virtualRows[vi.index];
                    if (row.kind === "header") {
                      return (
                        <div
                          key={row.key}
                          className={styles.virtualRow}
                          data-index={vi.index}
                          ref={rowVirtualizer.measureElement}
                          style={{
                            height: vi.size,
                            transform: `translateY(${vi.start}px)`,
                          }}
                        >
                          <div
                            className={styles.sectionHeader}
                            role="presentation"
                          >
                            {row.title}
                          </div>
                        </div>
                      );
                    }

                    return (
                      <div
                        key={row.key}
                        className={styles.virtualRow}
                        data-index={vi.index}
                        ref={rowVirtualizer.measureElement}
                        style={{
                          height: vi.size,
                          transform: `translateY(${vi.start}px)`,
                        }}
                      >
                        <div
                          role="option"
                          id={`${listboxId}-opt-${row.flatIndex}`}
                          aria-selected={row.flatIndex === activeFlatIndex}
                          className={[
                            styles.itemRow,
                            row.flatIndex === activeFlatIndex
                              ? styles.itemRowActive
                              : "",
                          ]
                            .filter(Boolean)
                            .join(" ")}
                          onMouseDown={(e) => e.preventDefault()}
                          onClick={() => selectItem(row.item)}
                        >
                          <div className={styles.itemBody}>
                            <div className={styles.itemRowContent}>
                              <PathSegmentTypeIcon
                                contextType={row.leafType}
                                isListItem={true}
                              />
                              {/*
                            Per-segment highlightMatch: queries spanning a segment
                            boundary may not highlight as one continuous span.
                          */}
                              <div className={styles.breadcrumbPath}>
                                {row.display.prefixRef ? (
                                  <span
                                    className={[
                                      styles.breadcrumbSegment,
                                      styles.viewSegmentStatic,
                                    ].join(" ")}
                                  >
                                    {row.display.prefixRef}
                                  </span>
                                ) : null}
                                {row.display.folderTypeLabel ? (
                                  <span
                                    className={[
                                      styles.breadcrumbSegment,
                                      styles.viewSegmentStatic,
                                    ].join(" ")}
                                  >
                                    {row.display.folderTypeLabel}
                                  </span>
                                ) : null}
                                {row.display.segments.length === 0 ? (
                                  !row.display.folderTypeLabel && !row.display.prefixRef ? (
                                    <span className={styles.breadcrumbSegment}>
                                      —
                                    </span>
                                  ) : null
                                ) : (
                                  row.display.segments.map((segment, i) => {
                                    const segmentContent = queryTrimmed
                                      ? highlightMatch(
                                          segment,
                                          inputValue,
                                          styles.markHighlight
                                        )
                                      : segment;
                                    const showChevron =
                                      !!row.display.folderTypeLabel || i > 0;
                                    return (
                                      <React.Fragment
                                        key={`${row.key}-seg-${i}`}
                                      >
                                        {showChevron ? (
                                          <span
                                            className={styles.viewPathSep}
                                            aria-hidden
                                          >
                                            <ChevronRightIcon
                                              size="S"
                                              UNSAFE_className={
                                                styles.viewPathChevron
                                              }
                                            />
                                          </span>
                                        ) : null}
                                        <span
                                          className={styles.breadcrumbSegment}
                                        >
                                          {segmentContent}
                                        </span>
                                      </React.Fragment>
                                    );
                                  })
                                )}
                              </div>
                            </div>
                          </div>
                        </div>
                      </div>
                    );
                  })}
                </div>
              )}
            </div>
            {showAddFooter ? (
              <div className={styles.addFooter}>
                <button
                  type="button"
                  className={styles.addButton}
                  aria-label="Add"
                  disabled={!onAdd}
                  onClick={() => onAdd?.()}
                >
                  <svg
                    className={styles.addIcon}
                    viewBox="0 0 24 24"
                    aria-hidden
                    focusable="false"
                  >
                    <path
                      fill="currentColor"
                      d="M19 13h-6v6h-2v-6H5v-2h6V5h2v6h6v2z"
                    />
                  </svg>
                  Add
                </button>
              </div>
            ) : null}
          </div>
        </div>
      )}
    </div>
  );
}

export default ContextSelection;
